import styles from '../../../components/RiskMatrix.module.scss';
import { useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { Text } from '../../../components/typography';
import { calculateRisk, colorLookup } from '../../../utilities/riskMatrix';
import { Rating, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import Icon from '../../../components/Icon';
import { t } from 'i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import TextWithIcon from '../../../components/typography/TextWithIcon';
import { openFileViewer } from '../../../graphql/cache/modal';
import { customColors } from '../../../utilities';
import Spacer from '../../../components/typography/Spacer';
import classNames from 'classnames';

function getLevelForValue(value, levels = {}) {
  return (
    Object.entries(levels).find(
      ([level, { min, max }]) =>
        typeof min === 'number' &&
        typeof max === 'number' &&
        value >= min &&
        value <= max,
    )?.[0] || null
  );
}

export default function RiskMatrixNoAuth({
  showMessage = true,
  onChange = () => {},
  impact = null,
  probability = null,
  readOnly = false,
  title = null,
  className,
  company = null,
}) {
  const {
    impact: impactData = [],
    probability: probabilityData = [],
    referenceFile = {},
  } = useMemo(() => company?.riskMatrix || {}, [company]);

  const renderCell = (p, i) => {
    const cellRisk = p * i;
    const cellSeverity = calculateRisk(cellRisk);
    const level = getLevelForValue(cellRisk, company?.riskMatrix?.levels);
    const cellLevelObj = company?.riskMatrix?.levels?.[level];

    const selected =
      p === probability && i === impact && cellRisk === selectedRisk;

    return (
      <div
        className={selected ? styles.highlight : styles.cell}
        onClick={() => {
          onChange(i, p);
        }}
        style={{
          backgroundColor: cellLevelObj
            ? colorLookup[level]
            : cellSeverity.color,
          opacity: selected ? 1 : 0.7,
        }}
      >
        <Text
          className={styles.hideOnMobile}
          noMargin
          noSelect
          color="white"
          size="sm"
          weight={selected ? 'semiBold' : 'regular'}
        >
          {level || cellSeverity.level}
        </Text>
      </div>
    );
  };

  const selectedRisk = useMemo(() => {
    return impact * probability;
  }, [impact, probability]);

  return readOnly ? (
    <div className={classNames(styles.center, className)}>
      {!!title && <Text weight="semiBold">{title}</Text>}
      <Text
        color={calculateRisk(selectedRisk)?.textColor}
        weight="bold"
        noMargin
        size="lg"
        noSelect
      >
        {!!selectedRisk
          ? `${calculateRisk(selectedRisk)?.level.toUpperCase()} RISK`
          : `${t(keys.common.NOT_COMPLETED)}`}
      </Text>
      <div className={styles.rating}>
        <Text textAlign="right" noMargin>
          Impact
        </Text>
        <Rating
          readOnly
          defaultValue={impact}
          max={5}
          size="small"
          emptyIcon={<Icon style={{ fontSize: '1rem' }}>circle</Icon>}
          icon={
            <CircleIcon
              sx={{
                fontSize: '1rem',
              }}
            />
          }
        />
      </div>
      <div className={styles.rating}>
        <Text textAlign="right" noMargin>
          Probability
        </Text>
        <Rating
          readOnly
          defaultValue={probability}
          max={5}
          size="small"
          emptyIcon={<Icon style={{ fontSize: '1rem' }}>circle</Icon>}
          icon={
            <CircleIcon
              sx={{
                fontSize: '1rem',
              }}
            />
          }
        />
      </div>
    </div>
  ) : (
    <div className={styles.container}>
      <div className={styles.mainHeader}>
        <TextWithIcon
          noMargin
          weight="bold"
          iconProps={{ color: customColors.BLUE }}
          hover={!!referenceFile}
          icon={!!referenceFile ? 'info' : ''}
          onClick={() => {
            openFileViewer({ ...referenceFile, downloadFile: true });
          }}
        >
          {title || 'Select Impact and Probability'}
        </TextWithIcon>
      </div>
      <Spacer />
      <Text noMargin noSelect color="secondary" textAlign="center" size="sm">
        Consequence/Impact
      </Text>
      <div className={styles.tableDisplay}>
        <div className={styles.rotateContainer}>
          <Text
            className={styles.rotate}
            noMargin
            noSelect
            color="secondary"
            size="sm"
          >
            Likelihood/Probability
          </Text>
        </div>
        <Table className={styles.table}>
          <thead>
            <tr>
              <th></th>
              {impactData.map((i, index) => (
                <th key={index}>
                  <Tooltip
                    title={
                      <Text weight="semiBold" noMargin color="white">
                        {i.message}
                      </Text>
                    }
                  >
                    <div
                      onClick={() => {
                        onChange(i.value, probability);
                      }}
                      className={
                        i.value === impact
                          ? styles.headerHighlight
                          : styles.header
                      }
                    >
                      <Text
                        noMargin
                        size="sm"
                        noSelect
                        className={styles.hideOnMobile}
                      >
                        {i.impact}
                      </Text>
                      <Text
                        noMargin
                        size="sm"
                        noSelect
                        className={styles.hideOnDesktop}
                      >
                        {i.value}
                      </Text>
                    </div>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {probabilityData.map((p, index) => (
              <tr key={index}>
                <th>
                  <Tooltip
                    title={
                      <Text weight="semiBold" noMargin color="white">
                        {p.message}
                      </Text>
                    }
                  >
                    <div
                      onClick={() => {
                        onChange(impact, p.value);
                      }}
                      className={
                        p.value === probability
                          ? styles.headerHighlight
                          : styles.header
                      }
                    >
                      <Text
                        noMargin
                        size="sm"
                        noSelect
                        className={styles.hideOnMobile}
                      >
                        {p.probability}
                      </Text>
                      <Text
                        noMargin
                        size="sm"
                        noSelect
                        className={styles.hideOnDesktop}
                      >
                        {p.value}
                      </Text>
                    </div>
                  </Tooltip>
                </th>
                {impactData.map((i, index) => (
                  <td key={index}>{renderCell(p.value, i.value)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {Object.keys(calculateRisk(selectedRisk))?.length > 0 && (
        <div
          className={styles.message}
          style={{
            display:
              Object.keys(calculateRisk(selectedRisk))?.length > 0
                ? 'block'
                : 'none',
          }}
        >
          <Text
            color={calculateRisk(selectedRisk)?.textColor}
            weight="bold"
            noMargin
            size="lg"
            noSelect
            textAlign="center"
          >
            {`${calculateRisk(selectedRisk)?.level.toUpperCase()} RISK`}
          </Text>
          {showMessage &&
            selectedRisk > 0 &&
            (() => {
              const selectedLevel = getLevelForValue(
                selectedRisk,
                company?.riskMatrix?.levels,
              );
              const message =
                company?.riskMatrix?.levels?.[selectedLevel]?.message;
              return (
                <Text color="secondary" size="sm" textAlign="center" noMargin>
                  {message || calculateRisk(selectedRisk)?.message}
                </Text>
              );
            })()}
        </div>
      )}
    </div>
  );
}
